import { useState } from 'react'
import { Badge } from '@/shared/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Switch } from '@/shared/ui/switch'
import { Label } from '@/shared/ui/label'
import { t } from '@/shared/lib/i18n'
import type { WebInstrumentsLotComparison } from '@/shared/types/web'
import { type LifecycleStatus, LIFECYCLE_BADGE } from '@/entities/lot/model/lifecycleStatus'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'

type LotDetailCard = WebInstrumentsLotComparison['primaryLotDetail']

const DATE_KEYS = new Set<keyof LotDetailCard>([
  'expirationDate',
  'openedExpiration',
  'dateReceived',
  'dateOpened',
  'dateInUse',
  'dateDiscarded',
])

const COMPARISON_FIELDS: Array<{ key: keyof LotDetailCard; labelKey: string }> = [
  { key: 'lotNumber', labelKey: 'instruments.lotsCompare.fields.lotNumber' },
  { key: 'manufacturer', labelKey: 'instruments.lotsCompare.fields.manufacturer' },
  { key: 'product', labelKey: 'instruments.lotsCompare.fields.product' },
  { key: 'lifecycleStatus', labelKey: 'instruments.lotsCompare.fields.status' },
  { key: 'expirationDate', labelKey: 'instruments.lotsCompare.fields.expiration' },
  { key: 'openedExpiration', labelKey: 'instruments.lotsCompare.fields.openedExpiration' },
  { key: 'dateReceived', labelKey: 'instruments.lotsCompare.fields.dateReceived' },
  { key: 'dateOpened', labelKey: 'instruments.lotsCompare.fields.dateOpened' },
  { key: 'siteName', labelKey: 'instruments.lotsCompare.fields.site' },
  { key: 'isConfirmed', labelKey: 'instruments.lotsCompare.fields.isConfirmed' },
  { key: 'comments', labelKey: 'instruments.lotsCompare.fields.comments' },
]

const renderCellValue = (fieldKey: keyof LotDetailCard, value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>

  if (fieldKey === 'lifecycleStatus') {
    const status = value as LifecycleStatus
    return (
      <Badge variant={LIFECYCLE_BADGE[status] ?? 'outline'}>
        {t(`instruments.lotsIndex.lifecycleStatus.${status}` as Parameters<typeof t>[0])}
      </Badge>
    )
  }

  if (DATE_KEYS.has(fieldKey)) {
    return formatCalendarDate(value as string)
  }

  if (typeof value === 'boolean') {
    return value ? t('instruments.lotsShow.yes') : t('instruments.lotsShow.no')
  }

  return String(value)
}

export interface LotCompareContentProps {
  comparison: WebInstrumentsLotComparison
}

export const LotCompareContent = ({ comparison }: LotCompareContentProps) => {
  const { primaryLotDetail, comparisonLotDetails, comparisonHistory } = comparison
  const [selectedId, setSelectedId] = useState<string>('')
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(false)

  const selected = comparisonLotDetails.find((ld) => ld.id.toString() === selectedId) ?? null

  const visibleFields = selected
    ? COMPARISON_FIELDS.filter(({ key }) => {
        if (!showDifferencesOnly) return true
        return primaryLotDetail[key] !== selected[key]
      })
    : COMPARISON_FIELDS

  const differenceCount = selected
    ? COMPARISON_FIELDS.filter(({ key }) => primaryLotDetail[key] !== selected[key]).length
    : 0

  return (
    <div className="space-y-5">
      {/* Primary lot identifier */}
      <div className="flex items-center gap-3 rounded-md bg-muted/50 px-4 py-3">
        <div className="text-sm text-muted-foreground">{t('instruments.lotsCompare.primary')}:</div>
        <span className="font-semibold">
          {primaryLotDetail.lotNumber ?? primaryLotDetail.lotName ?? `#${primaryLotDetail.id}`}
        </span>
        <Badge variant={LIFECYCLE_BADGE[primaryLotDetail.lifecycleStatus] ?? 'outline'}>
          {t(
            `instruments.lotsIndex.lifecycleStatus.${primaryLotDetail.lifecycleStatus}` as Parameters<
              typeof t
            >[0],
          )}
        </Badge>
      </div>

      {/* Comparison lot selector */}
      <div className="flex items-end gap-4">
        <div className="flex-1 space-y-1.5">
          <Label className="text-sm font-medium">
            {t('instruments.lotsCompare.compareAgainst')}
          </Label>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger>
              <SelectValue placeholder={t('instruments.lotsCompare.selectPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {comparisonLotDetails.map((ld) => (
                <SelectItem key={ld.id} value={ld.id.toString()}>
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{ld.lotNumber ?? ld.lotName ?? `#${ld.id}`}</span>
                    <Badge
                      variant={LIFECYCLE_BADGE[ld.lifecycleStatus] ?? 'outline'}
                      className="text-[10px] px-1.5 py-0"
                    >
                      {t(
                        `instruments.lotsIndex.lifecycleStatus.${ld.lifecycleStatus}` as Parameters<
                          typeof t
                        >[0],
                      )}
                    </Badge>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Comparison table */}
      {selected ? (
        <section className="space-y-3">
          {/* Toolbar: diff toggle + summary */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {differenceCount === 0
                ? t('instruments.lotsCompare.noDifferences')
                : t('instruments.lotsCompare.differenceCount', { count: differenceCount })}
            </p>
            <div className="flex items-center gap-2">
              <Switch
                id="diff-only"
                checked={showDifferencesOnly}
                onCheckedChange={setShowDifferencesOnly}
              />
              <Label htmlFor="diff-only" className="text-sm cursor-pointer">
                {t('instruments.lotsCompare.showDifferencesOnly')}
              </Label>
            </div>
          </div>

          {visibleFields.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <Table className="table-fixed">
                <colgroup>
                  <col className="w-[28%]" />
                  <col className="w-[36%]" />
                  <col className="w-[36%]" />
                </colgroup>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="font-semibold">
                      {t('instruments.lotsCompare.field')}
                    </TableHead>
                    <TableHead className="font-semibold">
                      {primaryLotDetail.lotNumber ?? t('instruments.lotsCompare.primary')}
                    </TableHead>
                    <TableHead className="font-semibold">
                      {selected.lotNumber ?? t('instruments.lotsCompare.selected')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleFields.map(({ key, labelKey }) => {
                    const differs = primaryLotDetail[key] !== selected[key]
                    return (
                      <TableRow
                        key={key}
                        className={differs ? 'bg-amber-50 dark:bg-amber-950/20' : ''}
                      >
                        <TableCell className="font-medium text-muted-foreground">
                          {t(labelKey as Parameters<typeof t>[0])}
                        </TableCell>
                        <TableCell>{renderCellValue(key, primaryLotDetail[key])}</TableCell>
                        <TableCell className={differs ? 'font-medium text-foreground' : ''}>
                          {renderCellValue(key, selected[key])}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t('instruments.lotsCompare.allFieldsMatch')}
            </div>
          )}
        </section>
      ) : (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('instruments.lotsCompare.emptyState')}</p>
        </div>
      )}

      {/* Comparison history */}
      {comparisonHistory.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {t('instruments.lotsCompare.historyTitle')}
          </h3>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>{t('instruments.lotsCompare.fields.lotNumber')}</TableHead>
                  <TableHead>{t('instruments.lotsCompare.historyDate')}</TableHead>
                  <TableHead>{t('instruments.lotsCompare.validated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonHistory.map((entry) => (
                  <TableRow key={entry.comparisonId}>
                    <TableCell className="font-medium">{entry.lotNumber ?? '—'}</TableCell>
                    <TableCell>{entry.date ? formatCalendarDate(entry.date) : '—'}</TableCell>
                    <TableCell>
                      <Badge variant={entry.isValidate ? 'default' : 'secondary'}>
                        {entry.isValidate
                          ? t('instruments.lotsCompare.yes')
                          : t('instruments.lotsCompare.no')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}
    </div>
  )
}
